import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { LINE_PIN_LENGTH, type Line } from '@chatapp-inbox/shared';
import { fetchLineStatus, isApiError, registerLine } from '../../api';
import { lineName } from '../conversations';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { connectionErrorMessage } from './error-message';
import { Field } from './Field';
import { connectionKey, lineStatusKey } from './keys';
import { describeLineStatus } from './line-status-text';
import forms from './forms.module.css';
import styles from './LinesSection.module.css';

export const PIN_ERROR_TEXT = `O PIN tem ${LINE_PIN_LENGTH} dígitos. Use só números.`;
export const REGISTERED_TEXT = 'Número registrado. Conferindo a situação…';

/**
 * A situação de um número no serviço (na Meta: "Conectado" ou "Pendente") e, quando ele está pendente, o campo do PIN
 * para concluir o registro. Um número adicionado na Meta fica "Pendente" até ser registrado, e só então envia e recebe.
 *
 * O PIN (6 dígitos) é a verificação em duas etapas do número: quem registra pela primeira vez escolhe um; se o número já
 * tinha PIN, é o mesmo. É um segredo digitado aqui: vai ao servidor num único pedido, que o repassa e o esquece; a tela
 * o apaga depois de enviar e o campo é de senha, sem preenchimento automático.
 */
export function LineRegistration({ line }: { line: Line }): ReactElement {
  const queryClient = useQueryClient();
  const panelId = useId();
  const status = useStatusMessage();
  const pinRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const name = lineName(line);

  // Só consulta o serviço quando a pessoa abre o painel, e sempre pergunta de novo (a situação muda sozinha).
  const query = useQuery({
    queryKey: lineStatusKey(line.id),
    queryFn: () => fetchLineStatus(line.id),
    enabled: open,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const register = useMutation({
    mutationFn: (value: string) => registerLine(line.id, value),
    onSuccess: () => {
      setPin('');
      status.announce(REGISTERED_TEXT);
      void query.refetch();
    },
    onError: (failure) => {
      setError(
        connectionErrorMessage(failure, 'Não foi possível registrar o número. Tente de novo.'),
      );
      // 404: o número deixou de existir (a lista está velha).
      if (isApiError(failure) && failure.status === 404) {
        void queryClient.invalidateQueries({ queryKey: connectionKey });
      }
      // 5xx: o pedido saiu e a resposta não veio (ou veio estranha): o registro PODE ter sido concluído. Confere sozinho.
      if (isApiError(failure) && failure.status >= 500) void query.refetch();
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (register.isPending) return;
    setError(null);
    status.announce('');
    if (pin.length !== LINE_PIN_LENGTH) {
      setPinError(PIN_ERROR_TEXT);
      pinRef.current?.focus();
      return;
    }
    setPinError(null);
    register.mutate(pin);
  };

  const view = query.data ? describeLineStatus(query.data) : null;

  return (
    <div className={styles.registration}>
      <Button
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((was) => !was);
          setError(null);
        }}
      >
        {open ? `Ocultar a situação de ${name}` : `Situação do número ${name}`}
      </Button>
      <div id={panelId} hidden={!open} className={styles.statusPanel}>
        {open && query.isPending && <p className={forms.hint}>Consultando o serviço…</p>}
        {open && query.isError && (
          <>
            <p role="alert" className={forms.alert}>
              {connectionErrorMessage(
                query.error,
                'Não foi possível consultar a situação do número.',
              )}
            </p>
            <div className={forms.actions}>
              <Button busy={query.isFetching} onClick={() => void query.refetch()}>
                Tentar de novo
              </Button>
            </div>
          </>
        )}
        {open && view && (
          <>
            <p className={styles.statusHeadline}>{view.headline}</p>
            {view.details.length > 0 && (
              <ul className={styles.statusDetails}>
                {view.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            )}
            <div className={forms.actions}>
              <Button busy={query.isFetching} onClick={() => void query.refetch()}>
                {query.isFetching ? 'Consultando…' : 'Conferir de novo'}
              </Button>
            </div>
            {view.canRegister && (
              <form onSubmit={submit} noValidate className={styles.registerForm}>
                {view.caution && <p className={forms.hint}>{view.caution}</p>}
                <Field
                  ref={pinRef}
                  label={`PIN de ${LINE_PIN_LENGTH} dígitos`}
                  hint={`Escolha ${LINE_PIN_LENGTH} números e guarde-os: é a verificação em duas etapas do número. Se o número já tinha PIN, informe o mesmo. O CladvApp não guarda o PIN.`}
                  error={pinError}
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={LINE_PIN_LENGTH}
                  value={pin}
                  onChange={(event) => {
                    setPin(event.target.value.replace(/\D/g, '').slice(0, LINE_PIN_LENGTH));
                    setPinError(null);
                  }}
                />
                <div className={forms.actions}>
                  <Button type="submit" variant="primary" busy={register.isPending}>
                    {register.isPending ? 'Registrando…' : 'Registrar número'}
                  </Button>
                </div>
              </form>
            )}
          </>
        )}
        <StatusRegion message={status.message} />
        {error && (
          <p role="alert" className={forms.alert}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
